/**
 * BizBook Hero Card
 * Profile header with avatar, bio, level, and guild
 * "Your journey. Your hustle. Your legend."
 */

import { useState } from 'react';
import { User, Edit2, Check, X } from 'lucide-react';
import type { TISAUser, Guild } from '@/types/tisa-coins';
import { updateBio } from '@/lib/legend';
import { useToast } from '@/hooks/use-toast';

interface LegendHeroCardProps {
  user: TISAUser;
  guild: Guild | null;
  currentLevel: number;
  totalXP: number;
  onRefresh: () => void;
}

export function LegendHeroCard({ user, guild, currentLevel, totalXP, onRefresh }: LegendHeroCardProps) {
  const { toast } = useToast();
  const [isEditingBio, setIsEditingBio] = useState(false);
  const [bioText, setBioText] = useState(user.legend_data?.bio || '');
  const [isSaving, setIsSaving] = useState(false);

  const handleSaveBio = async () => {
    setIsSaving(true);
    const result = await updateBio(user.id, bioText);

    if (result.success) {
      toast({
        title: 'Bio Updated',
        description: result.message
      });
      setIsEditingBio(false);
      onRefresh();
    } else {
      toast({
        title: 'Error',
        description: result.message,
        variant: 'destructive'
      });
    }
    setIsSaving(false);
  };

  const handleCancelEdit = () => {
    setBioText(user.legend_data?.bio || '');
    setIsEditingBio(false);
  };

  return (
    <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-purple-900/60 to-gray-900/60 border-2 border-purple/40 p-8 mb-8 shadow-2xl">
      {/* Background Glow Effect */}
      <div className="absolute inset-0 bg-gradient-to-r from-purple/10 via-gold/10 to-purple/10 opacity-30 animate-pulse"></div>

      {/* Content */}
      <div className="relative z-10">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6 mb-6">
          {/* Left: Avatar + Name + Level */}
          <div className="flex items-center gap-6">
            {/* Avatar */}
            <div className="relative flex-shrink-0">
              {user.avatar_url ? (
                <img
                  src={user.avatar_url}
                  alt={user.name}
                  className="w-24 h-24 rounded-full border-4 border-purple/50 shadow-2xl object-cover"
                />
              ) : (
                <div className="w-24 h-24 rounded-full bg-gradient-to-br from-purple-600 to-purple-800 flex items-center justify-center border-4 border-purple/50 shadow-2xl">
                  <User className="w-12 h-12 text-white" />
                </div>
              )}
              {/* Level Badge Overlay */}
              <div className="absolute -bottom-2 -right-2 bg-gold text-white px-3 py-1 rounded-full text-xs font-bold border-2 border-white/50 shadow-lg">
                LVL {currentLevel}
              </div>
            </div>

            {/* Name, XP, Level */}
            <div>
              <h1 className="text-4xl font-cinzel font-black text-white mb-1 drop-shadow-lg">
                {user.name}
              </h1>
              <p className="text-lg text-emerald-300 font-orbitron font-bold">
                🌟 Level {currentLevel}
              </p>
              <p className="text-sm text-gray-300 mt-1">
                <span className="text-emerald-400 font-semibold">{totalXP} Total XP</span>
              </p>
            </div>
          </div>

          {/* Right: Guild Badge */}
          {guild && (
            <div className={`px-6 py-4 rounded-xl bg-gradient-to-br ${guild.color} bg-opacity-30 border-2 border-cyan-300/50 text-center shadow-xl`}>
              <div className="text-5xl mb-2 drop-shadow-lg">{guild.icon}</div>
              <p className="font-cinzel font-black text-white text-xl drop-shadow-md">{guild.name}</p>
              <p className="text-sm text-cyan-100 italic mt-2 font-medium">"{guild.motto}"</p>
            </div>
          )}
        </div>

        {/* Bio Section */}
        <div className="bg-black/30 rounded-xl p-5 border border-purple/30">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-lg font-cinzel font-bold text-purple flex items-center gap-2">
              📘 BizBook Profile
            </h3>
            {!isEditingBio && (
              <button
                onClick={() => setIsEditingBio(true)}
                className="text-sm text-purple hover:text-purple-300 flex items-center gap-1 transition-colors"
              >
                <Edit2 className="w-4 h-4" />
                Edit
              </button>
            )}
          </div>

          {isEditingBio ? (
            <div className="space-y-3">
              <textarea
                value={bioText}
                onChange={(e) => setBioText(e.target.value)}
                maxLength={200}
                rows={3}
                className="w-full bg-gray-900/50 border-2 border-purple/30 rounded-lg p-3 text-white placeholder-gray-400 focus:border-purple focus:outline-none resize-none"
                placeholder="Your journey. Your hustle. Your legend... (max 200 characters)"
              />
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-400">
                  {bioText.length}/200 characters
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                    className="px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50"
                  >
                    <X className="w-4 h-4" />
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveBio}
                    disabled={isSaving}
                    className="px-4 py-2 bg-purple hover:bg-purple-600 text-white rounded-lg flex items-center gap-1 transition-colors disabled:opacity-50"
                  >
                    <Check className="w-4 h-4" />
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-gray-300 leading-relaxed">
              {bioText ? (
                <p>{bioText}</p>
              ) : (
                <p className="text-gray-500 italic">
                  Start your BizBook journey! Click "Edit" to share your story.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
